import { writable } from 'svelte/store';
import type { ErrorResult } from '../utils/errors';

export interface ToastNotification {
	id: string;
	message: string;
	type: 'error' | 'warning' | 'success' | 'info';
	details?: string[];
	duration?: number;
	dismissible?: boolean;
}

// Store for managing toast notifications
const { subscribe, update } = writable<ToastNotification[]>([]);

let nextId = 1;

function createToast(
	message: string,
	type: ToastNotification['type'] = 'info',
	options: {
		details?: string[];
		duration?: number;
		dismissible?: boolean;
	} = {}
): string {
	const id = `toast-${nextId++}`;
	const toast: ToastNotification = {
		id,
		message,
		type,
		duration: options.duration ?? (type === 'error' ? 0 : 5000), // Errors don't auto-dismiss
		dismissible: options.dismissible ?? true,
		...options
	};

	update(toasts => [...toasts, toast]);

	// Auto-dismiss if duration is set
	if (toast.duration && toast.duration > 0) {
		setTimeout(() => {
			dismiss(id);
		}, toast.duration);
	}

	return id;
}

function dismiss(id: string) {
	update(toasts => toasts.filter(toast => toast.id !== id));
}

function clear() {
	update(() => []);
}

// Helper functions for different toast types
export const toast = {
	subscribe,
	
	success: (message: string, options?: { details?: string[]; duration?: number }) => 
		createToast(message, 'success', options),
		
	error: (message: string, options?: { details?: string[]; duration?: number }) => 
		createToast(message, 'error', options),
		
	warning: (message: string, options?: { details?: string[]; duration?: number }) => 
		createToast(message, 'warning', options),
		
	info: (message: string, options?: { details?: string[]; duration?: number }) => 
		createToast(message, 'info', options),
		
	// Helper for API errors
	apiError: (errorResult: ErrorResult) => 
		createToast(errorResult.message, errorResult.type as any, {
			details: errorResult.details
		}),
		
	dismiss,
	clear
};

export default toast;